import { useEffect, useState } from 'react';
import LocalVideo from './LocalVideo';
import DiaryGallery from './DiaryGallery';
import { bootstrapSections, fetchSections } from './contentApi';
import fallbackSections from './homeSectionsFallback.json';

/**
 * The home page media stack — every photo and video band, in admin-defined order.
 *
 * The markup mirrors the original site components (hero-banner / wistia container)
 * so the existing stylesheet drives the layout and the overlay typography.
 */
function HomeMediaSections({ fallback = fallbackSections }) {
  // Laravel inlines the live sections into the page, so the first render is already
  // correct and no request is made. The fetch below only runs without that payload.
  const [sections, setSections] = useState(bootstrapSections || fallback);

  useEffect(() => {
    if (bootstrapSections) return undefined;

    let active = true;
    fetchSections()
      .then((list) => { if (active && list.length) setSections(list); })
      .catch(() => { /* API offline: keep the bundled fallback */ });
    return () => { active = false; };
  }, []);

  const visible = sections.filter((section) => section.enabled !== false);
  if (!visible.length) return null;

  return visible.map((section) => {
    if (section.type === 'video') return <VideoSection key={section.id} section={section} />;
    if (section.type === 'diary') return <DiaryGallery key={section.id} section={section} />;
    if (section.type === 'gallery') return <GallerySection key={section.id} section={section} />;
    return <PhotoSection key={section.id} section={section} />;
  });
}

/** Overlay text colour classes borrowed from the original components. */
function themeClass(section) {
  return section.theme === 'dark' ? 'DeskNoirMobNoir' : 'DeskBlancMobBlanc';
}

function PhotoSection({ section }) {
  const label = section.alt || section.title || 'MONALISA LIVING';
  const href = section.ctaHref || undefined;

  const image = (
    <picture>
      {section.mobile && <source srcSet={section.mobile} media="(max-width: 833.98px)" />}
      <img className="banner-image" src={section.desktop} alt={label} loading="lazy" decoding="async" />
    </picture>
  );

  return (
    <div className="experience-component experience-assets-mainBanner admin-managed-section">
      <div className="hero-banner no-gutters">
        <div className="hero-banner-image-wrapper align-self-start">
          {href ? <a href={href}>{image}</a> : image}
        </div>
        {(section.title || section.subtitle || section.ctaLabel) && (
          <div className="hero-banner-overlay-wrapper">
            <div className="hero-banner-overlay d-flex p-0">
              <div className="hero-banner-overlay-inner text-left">
                <div className={`inner-overlay no-gutters pos-a top-0 start-0 ${themeClass(section)}`}>
                  {section.title && <h2 className="banner-heading">{section.title}</h2>}
                  {section.subtitle && <p className="banner-subheading mb-2">{section.subtitle}</p>}
                  {section.ctaLabel && (
                    <a href={href || '#'} className="link banner-link">
                      <span aria-hidden="true" className="chevron-right">›</span>
                      <span className="label">{section.ctaLabel}</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Mosaic collage band — a dense CSS grid where each tile claims 1 or 2 columns/rows,
 * so uploaded photos tile together without gaps regardless of their aspect ratios.
 */
function GallerySection({ section }) {
  const tiles = (section.images || []).filter((tile) => tile.src);
  if (!tiles.length) return null;

  return (
    <section className="experience-component admin-managed-section photo-mosaic-section"
      aria-label={section.alt || section.title || 'Gallery'}>
      {(section.title || section.subtitle) && (
        <header className={'photo-mosaic-head ' + themeClass(section)}>
          {section.title && <h2 className="banner-heading">{section.title}</h2>}
          {section.subtitle && <p className="banner-subheading">{section.subtitle}</p>}
        </header>
      )}

      <div className="photo-mosaic" style={{ '--mosaic-gap': (section.gap ?? 8) + 'px' }}>
        {tiles.map((tile, index) => {
          const image = (
            <img src={tile.src} alt={tile.alt || ''} loading="lazy" decoding="async" />
          );
          return (
            <figure key={tile.id || tile.src + index} className={'photo-mosaic-tile is-' + (tile.size || 'small')}>
              {tile.href ? <a href={tile.href}>{image}</a> : image}
            </figure>
          );
        })}
      </div>

      {section.ctaLabel && (
        <div className="photo-mosaic-cta">
          <a href={section.ctaHref || '#'} className="link banner-link">
            <span aria-hidden="true" className="chevron-right">›</span>
            <span className="label">{section.ctaLabel}</span>
          </a>
        </div>
      )}
    </section>
  );
}

function VideoSection({ section }) {
  const label = section.alt || section.title || 'MONALISA LIVING';

  return (
    <div className="experience-component experience-assets-playerVideoWistiaSecondary admin-managed-section">
      <div className="wistia_container_secondary_mobile justify-content-start align-items-start text-left wistia-viewport react-video-ready"
        style={{ '--wistia-native-w': '100%', '--wistia-native-h': '100%' }}>
        <LocalVideo src={section.src} poster={section.poster} label={label} />
        {(section.title || section.subtitle || section.ctaLabel) && (
          <div className="wistia_overlay no-gutters">
            <div className="inner-overlay" style={{ backgroundColor: 'transparent' }}>
              {section.title && <h2 className={themeClass(section)}><strong>{section.title}</strong></h2>}
              {section.subtitle && (
                <p className={themeClass(section)} style={{ lineHeight: '1.4' }}>{section.subtitle}</p>
              )}
              {section.ctaLabel && (
                <a href={section.ctaHref || '#'} className={themeClass(section)} style={{ padding: 0, margin: 0 }}>
                  <span aria-hidden="true" className="chevron-right">›</span>
                  <span className="label">{section.ctaLabel}</span>
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HomeMediaSections;
